import React, { useState, FormEvent, ReactNode } from 'react';
import styles from './ForgotPassword.module.scss';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { MessageBanner } from 'components/helpers/Atoms/FormElements/MessageBanner';
import { FormField } from 'components/helpers/Atoms/FormElements/FormField';
import { TextInput } from 'components/helpers/Atoms/FormElements/TextInput';
import clsx from 'clsx';
import useDictionary from 'lib/hooks/useDictionary';
import { useFormErrors } from 'lib/hooks/useFormErrors';

const FORM_FIELD_IDS = {
  EMAIL: 'forgotPasswordEmail',
} as const;

export interface ForgotPasswordProps {
  title: ReactNode;
  description: ReactNode;
  signupLink: ReactNode;
  cancelLink: ReactNode;
  emailAddressLabel: ReactNode;
  emailPlaceholderText: string;
  submitButtonText: ReactNode;
  successMessage: ReactNode;
  notAMemberText: ReactNode;
}

export default function ForgotPassword(props: ForgotPasswordProps) {
  const {
    title,
    description,
    submitButtonText,
    successMessage,
    emailPlaceholderText,
    signupLink,
    cancelLink,
    emailAddressLabel,
    notAMemberText,
  } = props;

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const { getDictionaryValue } = useDictionary();
  const { setFieldError, clearFieldError, clearAllErrors, getFieldError } = useFormErrors();

  const validateEmail = (emailValue: string): string => {
    if (!emailValue.trim()) {
      const requiredMessage = getDictionaryValue(
        'Components.ForgotPassword.EmailRequired',
        'Please enter your email address.'
      );
      setFieldError(FORM_FIELD_IDS.EMAIL, requiredMessage);
      return requiredMessage;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      const invalidMessage = getDictionaryValue(
        'Components.ForgotPassword.InvalidEmail',
        'This email is not valid. Please re-enter your email address and try again.'
      );
      setFieldError(FORM_FIELD_IDS.EMAIL, invalidMessage);
      return invalidMessage;
    }
    clearFieldError(FORM_FIELD_IDS.EMAIL);
    return '';
  };

  const focusEmailField = () => {
    const el = document.getElementById(FORM_FIELD_IDS.EMAIL) as HTMLInputElement | null;
    if (!el) return;

    // Wait for state updates (inline error + aria-describedby) to land before focusing,
    // so the screen reader announces the field error when focus returns.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        el.focus();
      });
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setHasAttemptedSubmit(true);

    // Clear previous errors and validate
    clearAllErrors();
    const aggregatedErrors: string[] = [];
    const emailError = validateEmail(email);
    if (emailError) {
      aggregatedErrors.push(emailError);
    }

    if (aggregatedErrors.length > 0) {
      const bannerMessage = getDictionaryValue(
        'Components.ForgotPassword.EmptyFieldsSubmission',
        'There was an error resetting your password.| Please check the below errors and try again.'
      );
      const summarizedErrors = Array.from(new Set(aggregatedErrors)).join(' ');
      setSubmitError(bannerMessage ? `${bannerMessage} `.trim() : summarizedErrors);

      // Return focus to the first field with an error.
      if (emailError) {
        focusEmailField();
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const requestBody = {
        email: email.trim(),
      };

      const response = await fetch('/api/auth/send-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 200) {
        // Success
        setSubmitSuccess(true);
        setEmail('');
        clearAllErrors();
        setHasAttemptedSubmit(false);
      } else {
        // Error - show generic error message
        const errorMessage = getDictionaryValue(
          'Components.ForgotPassword.PasswordResetFailed',
          'There was an error resetting your password.| Please Try Again.'
        );
        setSubmitError(errorMessage);
      }
    } catch (_error) {
      const errorMessage = getDictionaryValue(
        'Components.ForgotPassword.PasswordResetFailed',
        'There was an error resetting your password.| Please Try Again.'
      );
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const emailError: string | undefined = hasAttemptedSubmit
    ? (getFieldError(FORM_FIELD_IDS.EMAIL) as string | undefined)
    : undefined;
  const emailErrorId = `${FORM_FIELD_IDS.EMAIL}-error`;
  const hasEmailError = Boolean(emailError);

  return (
    <div className={styles.forgotPasswordContainer}>
      <div className={styles.forgotPassword}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>{title}</h1>
            {description && (
              <div className={styles.notAMemberDescription}>
                {notAMemberText}
                {signupLink && <span className={styles.signupLink}>{signupLink}</span>}
              </div>
            )}
          </div>
          <hr className={styles.divider} />
        </div>

        <div className={styles.content}>
          {submitSuccess ? (
            <div className={styles.successMessage}>{successMessage}</div>
          ) : (
            <>
              {submitError && (
                <div className={styles.errorBannerWrapper}>
                  <MessageBanner type="error" message={submitError} />
                </div>
              )}

              {description && <div className={styles.description}>{description}</div>}

              <div className={styles.requiredIndicatorWrapper}>
                <span className={styles.requiredIndicator}>
                  {getDictionaryValue('Components.MyAccount.Required', '* Required')}
                </span>
              </div>

              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={styles.inputs}>
                  <div className={clsx(styles.formFields)}>
                    <FormField
                      label={emailAddressLabel}
                      labelFor={FORM_FIELD_IDS.EMAIL}
                      error={emailError}
                      showInlineError={hasAttemptedSubmit && hasEmailError}
                      errorId={emailErrorId}
                      required
                    >
                      <TextInput
                        id={FORM_FIELD_IDS.EMAIL}
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        placeholder={emailPlaceholderText}
                        error={hasAttemptedSubmit && hasEmailError}
                        errorId={emailErrorId}
                        disabled={isSubmitting}
                        required
                      />
                    </FormField>
                  </div>
                </div>

                <div className={styles.actions}>
                  <CtaButton
                    ctaStyle="filled"
                    ctaVariant="brand"
                    type="submit"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {submitButtonText}
                  </CtaButton>

                  <div className={styles.cancelLinkWrapper}>{cancelLink}</div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
